// ============================================================
// Modal helper — opens an overlay with header / body / footer.
// Returns { close } so the caller can dismiss programmatically.
// Backdrop click + Escape close by default; pass closeOnBackdrop:false
// to lock (used for the BOM error overlay which prefers an explicit
// "Understood" button).
// ============================================================

import { el } from '../ui.js';

let _activeStack = [];  // supports stacking (e.g. stock modal → BOM confirm modal)

function _onKeydown(e) {
  if (e.key !== 'Escape') return;
  const top = _activeStack[_activeStack.length - 1];
  if (top && top.closeOnEscape !== false) top.close();
}

document.addEventListener('keydown', _onKeydown);

export function openModal({
  title,
  body,                // HTMLElement OR string (innerHTML)
  footer,              // HTMLElement OR string OR null
  size = 'md',         // 'sm' | 'md' | 'lg' | 'xl'
  closeOnBackdrop = true,
  closeOnEscape = true,
  variant = '',        // '' | 'danger'
  onClose
} = {}) {
  const overlay = el('div', { class: 'modal-overlay', tabindex: '-1' });
  const card    = el('div', { class: `modal-card modal-${size} ${variant ? 'modal-' + variant : ''}` });

  const headerEl = el('div', { class: 'modal-header' },
    el('div', { class: 'modal-title' }, title || ''),
    el('button', { class: 'modal-close', type: 'button', 'aria-label': 'Close' }, '✕')
  );

  const bodyEl = el('div', { class: 'modal-body' });
  if (body) {
    if (typeof body === 'string') bodyEl.innerHTML = body;
    else bodyEl.appendChild(body);
  }

  card.appendChild(headerEl);
  card.appendChild(bodyEl);

  let footerEl = null;
  if (footer) {
    footerEl = el('div', { class: 'modal-footer' });
    if (typeof footer === 'string') footerEl.innerHTML = footer;
    else if (Array.isArray(footer)) footer.forEach(f => footerEl.appendChild(f));
    else footerEl.appendChild(footer);
    card.appendChild(footerEl);
  }

  overlay.appendChild(card);
  document.body.appendChild(overlay);

  // Focus first input or the close button
  setTimeout(() => {
    const firstInput = bodyEl.querySelector('input, select, textarea, button');
    (firstInput || headerEl.querySelector('.modal-close')).focus();
  }, 50);

  const handle = {
    overlay,
    card,
    bodyEl,
    closeOnEscape,
    close() {
      const idx = _activeStack.indexOf(handle);
      if (idx >= 0) _activeStack.splice(idx, 1);
      overlay.remove();
      if (typeof onClose === 'function') onClose();
    },
    setBody(newBody) {
      bodyEl.innerHTML = '';
      if (typeof newBody === 'string') bodyEl.innerHTML = newBody;
      else bodyEl.appendChild(newBody);
    },
    setBusy(isBusy) {
      card.classList.toggle('busy', !!isBusy);
    }
  };

  headerEl.querySelector('.modal-close').addEventListener('click', () => handle.close());
  if (closeOnBackdrop) {
    overlay.addEventListener('click', (e) => { if (e.target === overlay) handle.close(); });
  }

  _activeStack.push(handle);
  return handle;
}

/** Confirm dialog — returns Promise<boolean>. */
export function confirmModal({ title, message, okText = 'Confirm', cancelText = 'Cancel', variant = '' }) {
  return new Promise((resolve) => {
    const okBtn     = el('button', { class: 'btn ' + (variant === 'danger' ? 'btn-danger' : 'btn-primary') }, okText);
    const cancelBtn = el('button', { class: 'btn btn-secondary' }, cancelText);

    const handle = openModal({
      title,
      body: el('div', { class: 'confirm-msg' }, message),
      footer: [cancelBtn, okBtn],
      size: 'sm',
      variant
    });

    okBtn.addEventListener('click', () => { handle.close(); resolve(true); });
    cancelBtn.addEventListener('click', () => { handle.close(); resolve(false); });
  });
}
